import React, {useState, useEffect, useRef} from "react"
import { apiService } from '../../services/api'
import styles from './SearchFilters.module.css'

const SearchFilters = ({ filters, onFiltersChange }) => {
  const [categories, setCategories] = useState([])
  const [priceRange, setPriceRange] = useState({
    min: filters.min_price ?? '',
    max: filters.max_price ?? ''
  })
  const [tempFilters, setTempFilters] = useState(filters)
  const debounceRef = useRef()

  useEffect(() => {
    apiService.getCategories()
      .then(response => {
        if (Array.isArray(response)) {
          setCategories(response)
        } else {
          setCategories(response.categories || [])
        }
      })
      .catch(error => {
        console.error('Error fetching categories:', error)
      })
  }, [])

  useEffect(() => {
    setTempFilters(filters)
    setPriceRange({
      min: filters.min_price ?? '',
      max: filters.max_price ?? ''
    })
  }, [filters])
  
  const handleFilterChange = (key, value) => {
    let newFilters = { ...tempFilters };
    // Para checkboxes, poner en false en vez de eliminar
    if (typeof value === 'boolean') {
      newFilters[key] = value;
    } else if (value === '' || value === null) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    // Si el filtro es categoría, usar slug
    if (key === 'category_id') {
      const selectedCat = categories.find(cat => cat.id === value);
      if (selectedCat) {
        newFilters.category_slug = selectedCat.slug;
      } else {
        delete newFilters.category_slug;
      }
      delete newFilters.category_id;
    }
    // Si el filtro es ordenamiento, usar sort_by y sort_order
    if (key === 'sortBy' || key === 'sortOrder') {
      if (newFilters.sortBy) {
        newFilters.sort_by = newFilters.sortBy;
      }
      if (newFilters.sortOrder) {
        newFilters.sort_order = newFilters.sortOrder;
      }
      delete newFilters.sortBy;
      delete newFilters.sortOrder;
    }
    // Elimina claves vacías/nulas
    Object.keys(newFilters).forEach(k => {
      if (newFilters[k] === '' || newFilters[k] === null) {
        delete newFilters[k];
      }
    });
    setTempFilters(newFilters);
    onFiltersChange({ ...newFilters });
    window.location.reload();
  };

  const handlePriceChange = (type, value) => {
    const newPriceRange = { ...priceRange, [type]: value }
    setPriceRange(newPriceRange)
    let newFilters = {
      ...tempFilters,
      min_price: newPriceRange.min !== '' ? newPriceRange.min : null,
      max_price: newPriceRange.max !== '' ? newPriceRange.max : null
    }
    // Elimina claves vacías/nulas
    Object.keys(newFilters).forEach(k => {
      if (newFilters[k] === '' || newFilters[k] === null) {
        delete newFilters[k];
      }
    });
    setTempFilters(newFilters)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      onFiltersChange({ ...newFilters })
      window.location.reload();
    }, 400)
  }

  const clearFilters = () => {
    setPriceRange({ min: '', max: '' });
    setTempFilters({});
    // Reinicia todos los filtros locales y dispara la limpieza en el padre
    onFiltersChange({});
    window.location.reload();
  }

  const hasActiveFilters = Object.values(filters).some(value => value != null && value !== '')
  // ...existing code...

  return (
    <div className={styles.searchFilters}>
      <div className={styles.filterHeader}>
        <h3>Filtros</h3>
        {hasActiveFilters && (
          <button className={styles.clearFilters} onClick={clearFilters}>
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Categorías */}
      <div className={styles.filterSection}>
        <h4>Categoría</h4>
        <select
          value={tempFilters.category_slug ? (categories.find(cat => cat.slug === tempFilters.category_slug)?.id || '') : ''}
          onChange={(e) => {
            const selectedCat = categories.find(cat => cat.id === e.target.value);
            if (selectedCat) {
              handleFilterChange('category_id', selectedCat.id);
            } else {
              handleFilterChange('category_id', '');
            }
          }}
          className={styles.filterSelect}
        >
          <option value="">Todas las categorías</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Rango de precios */}
      <div className={styles.filterSection}>
        <h4>Precio</h4>
        <div className={styles.priceRange}>
          <div className={styles.priceInput}>
            <label>Mínimo</label>
            <input
              type="number"
              placeholder="$0"
              value={priceRange.min}
              onChange={(e) => handlePriceChange('min', e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          <div className={styles.priceInput}>
            <label>Máximo</label>
            <input
              type="number"
              placeholder="$999"
              value={priceRange.max}
              onChange={(e) => handlePriceChange('max', e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
        </div>
        
        {/* Rangos predefinidos */}
        <div className={styles.pricePresets}>
          <button
            onClick={() => {
              setPriceRange({ min: '', max: '50' });
              handlePriceChange('min', '');
              handlePriceChange('max', '50');
            }}
            className={styles.pricePreset}
          >
            Menos de $50
          </button>
          <button
            onClick={() => {
              setPriceRange({ min: '50', max: '200' });
              handlePriceChange('min', '50');
              handlePriceChange('max', '200');
            }}
            className={styles.pricePreset}
          >
            $50 - $200
          </button>
          <button
            onClick={() => {
              setPriceRange({ min: '200', max: '500' });
              handlePriceChange('min', '200');
              handlePriceChange('max', '500');
            }}
            className={styles.pricePreset}
          >
            $200 - $500
          </button>
          <button
            onClick={() => {
              setPriceRange({ min: '500', max: '' });
              handlePriceChange('min', '500');
              handlePriceChange('max', '');
            }}
            className={styles.pricePreset}
          >
            Más de $500
          </button>
        </div>
      </div>

      {/* Ordenamiento */}
      <div className={styles.filterSection}>
        <h4>Ordenar por</h4>
        <select
          value={`${tempFilters.sort_by ?? 'created_at'}-${tempFilters.sort_order ?? 'desc'}`}
          onChange={(e) => {
            const [sortBy, sortOrder] = e.target.value.split('-');
            const newFilters = { ...tempFilters, sort_by: sortBy, sort_order: sortOrder };
            setTempFilters(newFilters);
            onFiltersChange(newFilters);
            window.location.reload()
          }}
          className={styles.filterSelect}
        >
          <option value="created_at-desc">Más recientes</option>
          <option value="created_at-asc">Más antiguos</option>
          <option value="price-asc">Precio: menor a mayor</option>
          <option value="price-desc">Precio: mayor a menor</option>
          <option value="name-asc">Nombre: A-Z</option>
          <option value="name-desc">Nombre: Z-A</option>
          <option value="rating-desc">Mejor valorados</option>
        </select>
      </div>

      {/* Filtros booleanos */}
      <div className={styles.filterSection}>
        <h4>Características</h4>
        <div className={styles.checkboxGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={!!tempFilters.isPrime}
              onChange={e => handleFilterChange('isPrime', e.target.checked)}
            />
            <span>Prime</span>
          </label>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={!!tempFilters.isFeatured}
              onChange={e => handleFilterChange('isFeatured', e.target.checked)}
            />
            <span>Productos destacados</span>
          </label>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={!!tempFilters.inStock}
              onChange={e => handleFilterChange('inStock', e.target.checked)}
            />
            <span>En stock</span>
          </label>
        </div>
      </div>

      {/* Calificación 
      <div className={styles.filterSection}>
        <h4>Calificación</h4>
        <div className={styles.ratingFilters}>
          {[4, 3, 2, 1].map((rating) => (
            <label key={rating} className={styles.ratingLabel}>
              <input
                type="radio"
                name="minRating"
                value={rating}
                checked={tempFilters.minRating === rating.toString()}
                onChange={(e) => handleFilterChange('minRating', e.target.value)}
              />
              <div className={styles.ratingStars}>
                {Array.from({ length: 5 }, (_, i) => (
                  <span
                    key={i}
                    className={i < rating ? styles.starFilled : styles.starEmpty}
                  >
                    ★
                  </span>
                ))}
                <span className={styles.ratingText}>y más</span>
              </div>
            </label>
          ))}
        </div>
      </div>*/}
    </div>
  )
}

export default SearchFilters